"use client";

import { clientApi } from "@frontend/lib/api";
import { clearReturnTo } from "@frontend/lib/return-to";

export type OfferCheckoutPayload = {
  checkoutId: string;
  orderId: string;
  amount: number;
  currency: string;
  keyId: string;
  tier: string;
  basePaise: number;
  gstPaise: number;
  totalPaise: number;
  contactPhone?: string | null;
  contactName?: string | null;
};

export type ActionState = {
  error?: string;
  ok?: boolean;
  redirectTo?: string;
  checkout?: OfferCheckoutPayload;
};
export type OtpRequestState = {
  error?: string;
  sent?: boolean;
  phone?: string;
  /** Present only when backend AUTH_OTP_LOCAL=1. */
  devCode?: string;
};
export type OtpVerifyState = {
  error?: string;
  redirectTo?: string;
  /** Inline offer-page auth: session is ready, refresh to show the form. */
  ready?: boolean;
  /** Inline offer-page auth: phone is new — collect display name + username. */
  needsProfile?: boolean;
};
export type LoginState = OtpRequestState & OtpVerifyState;
export type SignUpState = OtpRequestState & OtpVerifyState;
export type SetHandleState = { error?: string; ok?: boolean };

async function postForm<T extends ActionState>(path: string, formData: FormData): Promise<T> {
  const data = await clientApi<T>(path, { method: "POST", body: formData });
  if (data.redirectTo) {
    clearReturnTo();
    window.location.href = data.redirectTo;
  }
  return data;
}

export async function requestOtpAction(
  _state: OtpRequestState,
  formData: FormData,
): Promise<OtpRequestState> {
  return postForm("/api/auth/otp/request", formData);
}

export async function verifyLoginOtpAction(
  _state: OtpVerifyState,
  formData: FormData,
): Promise<OtpVerifyState> {
  return postForm("/api/auth/otp/verify-login", formData);
}

export async function verifySignUpOtpAction(
  _state: OtpVerifyState,
  formData: FormData,
): Promise<OtpVerifyState> {
  return postForm("/api/auth/otp/verify-signup", formData);
}

export async function completeCreatorProfileAction(
  _state: OtpVerifyState,
  formData: FormData,
): Promise<OtpVerifyState> {
  return postForm("/api/auth/complete-profile", formData);
}

export async function signOutAction() {
  const data = await clientApi<{ redirectTo?: string }>("/api/auth/sign-out", { method: "POST" });
  window.location.href = data.redirectTo ?? "/";
}

export async function setHandleAction(
  _state: SetHandleState,
  formData: FormData,
): Promise<SetHandleState> {
  return postForm("/api/handle/set", formData);
}

/**
 * Switching accounts changes what every server component on the page resolves to,
 * so the response drives a full navigation rather than a router refresh.
 */
export async function switchAccountAction(_state: ActionState, formData: FormData) {
  return postForm("/api/accounts/switch", formData);
}

export async function inviteMemberAction(_state: ActionState, formData: FormData) {
  return postForm("/api/team/invite", formData);
}

/** The invitee accepting or declining. `accept` is "true" or "false". */
export async function respondToInviteAction(_state: ActionState, formData: FormData) {
  return postForm("/api/invites/respond", formData);
}

export async function updateMemberRoleAction(_state: ActionState, formData: FormData) {
  return postForm("/api/team/role", formData);
}

export async function removeMemberAction(_state: ActionState, formData: FormData) {
  return postForm("/api/team/remove", formData);
}

export async function submitOfferAction(_state: ActionState, formData: FormData) {
  return postForm("/api/offers/submit", formData);
}

export async function confirmOfferBoostAction(_state: ActionState, formData: FormData) {
  return postForm("/api/offers/confirm-boost", formData);
}

export async function counterOfferAction(_state: ActionState, formData: FormData) {
  return postForm("/api/offers/counter", formData);
}

export async function acceptOfferAction(_state: ActionState, formData: FormData) {
  return postForm("/api/offers/accept", formData);
}

export async function sendMessageAction(_state: ActionState, formData: FormData) {
  return postForm("/api/collabs/message", formData);
}

export async function requestCallAction(_state: ActionState, formData: FormData) {
  return postForm("/api/collabs/calls/request", formData);
}

export async function enableCallsAction(_state: ActionState, formData: FormData) {
  return postForm("/api/collabs/calls/enable", formData);
}

export async function startCallAction(_state: ActionState, formData: FormData) {
  return postForm("/api/collabs/calls/start", formData);
}

export async function submitDraftAction(_state: ActionState, formData: FormData) {
  return postForm("/api/collabs/draft", formData);
}

export async function requestRevisionAction(_state: ActionState, formData: FormData) {
  return postForm("/api/collabs/revision", formData);
}

export async function approveDraftAction(_state: ActionState, formData: FormData) {
  return postForm("/api/collabs/approve-draft", formData);
}

export async function submitLiveUrlAction(_state: ActionState, formData: FormData) {
  return postForm("/api/collabs/live-url", formData);
}

export async function confirmLiveAction(_state: ActionState, formData: FormData) {
  return postForm("/api/collabs/confirm-live", formData);
}

export async function cancelCollabAction(_state: ActionState, formData: FormData) {
  return postForm("/api/collabs/cancel", formData);
}

export async function markPaidAction(_state: ActionState, formData: FormData) {
  return postForm("/api/payments/mark-paid", formData);
}

export async function confirmPaymentAction(_state: ActionState, formData: FormData) {
  return postForm("/api/payments/confirm", formData);
}

export async function rejectPaymentAction(_state: ActionState, formData: FormData) {
  return postForm("/api/payments/reject", formData);
}

export async function savePayoutDetailsAction(_state: ActionState, formData: FormData) {
  return postForm("/api/payout-details", formData);
}

export async function saveCreatorProfileAction(_state: ActionState, formData: FormData) {
  return postForm("/api/settings/profile", formData);
}

export async function revokeSessionAction(_state: ActionState, formData: FormData) {
  return postForm("/api/settings/sessions/revoke", formData);
}

export async function revokeOtherSessionsAction(_state: ActionState, formData: FormData) {
  return postForm("/api/settings/sessions/revoke-others", formData);
}
